import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
};

const steps = ['Company', 'Contact', 'Review'];

const GenericServiceForm = ({ serviceId, serviceName, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (step === 1 && !form.companyName) {
      toast({
        title: 'Required Field Missing',
        description: 'Please provide the company / entity name.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2 && (!form.contactName || !form.contactEmail)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Contact name and email are required.',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title={`${serviceName} Application`}
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Entity Information</h3>
          <div>
            <Label>Company / Entity Name *</Label>
            <Input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div>
            <Label>Registration / Reference Number</Label>
            <Input
              value={form.registrationNumber}
              onChange={(e) => set('registrationNumber', e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Contact Details</h3>
          <div>
            <Label>Contact Person *</Label>
            <Input
              value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={form.contactPhone}
                onChange={(e) => set('contactPhone', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Notes / Special Instructions</Label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[100px]"
              placeholder="Share any details relevant to this service..."
            />
          </div>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Service:</strong> {serviceName}</p>
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg/Ref No:</strong> {form.registrationNumber || '—'}</p>
            <p><strong>Contact:</strong> {form.contactName} — {form.contactEmail}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default GenericServiceForm;
